/**
 * COMPONENT: RoomBlocks.jsx
 * Room block management with status views and a richer admin UX.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "../api/axiosInstance";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePmsTheme } from "../Components/PmsThemeContext";
import useCrossTabDataRefreshToken from "../hooks/useCrossTabDataRefresh";
import { DEMO_BUSINESS_UNIT_ID, isDemoMode } from "../demo/demoMode";
import { demoReferenceDate } from "../demo/demoSnapshot";
import * as XLSX from "xlsx";
import "./RoomBlocks.css";

const STATUS_FILTERS = [
  "upcoming",
  "active",
  "scheduled",
  "past",
  "all",
];

const ROOM_BLOCKS_MESSAGES = {
  en: {
    demo: {
      readOnly: "Read-only demo",
      notIncluded: "Not included in the demo",
    },
    header: {
      kicker: "Operations cockpit",
      title: "Room Blocks",
      subtitle:
        "Block inventory with live status filtering, timeline visibility, and in-flow availability checks.",
    },
    buttons: {
      refresh: "Refresh",
      export: "Export Excel",
      newBlock: "New Block",
      createReservation: "Create reservation",
      cancel: "Cancel",
      resetForm: "Reset Form",
      confirmBlock: "Confirm Block",
      remove: "Remove",
      removing: "Removing...",
      closeModal: "Close modal",
    },
    kpis: {
      totalLabel: "Total Blocks",
      totalFoot: "All records in this business unit",
      activeLabel: "Active Today",
      activeFoot: "Currently impacting sellable inventory",
      scheduledLabel: "Scheduled",
      scheduledFoot: "Upcoming blocks not started yet",
      endingLabel: "Ending in 7 Days",
      endingFoot: "Good candidates to review or extend",
      pastLabel: "Past",
      pastFoot: "Historical records for traceability",
    },
    filters: {
      statusLabel: "Status Filter",
      actionsLabel: "Actions",
      searchPlaceholder: "Search by room, category, reason, user, or date...",
      allCategories: "All categories",
      options: {
        upcoming: "Upcoming",
        active: "Active",
        scheduled: "Scheduled",
        past: "Past",
        all: "All",
      },
    },
    states: {
      loadingTitle: "Loading room blocks...",
      loadingText: "Fetching rooms, statuses, and metadata.",
      loadErrorTitle: "Could not load data",
      emptyTitle: "No blocks found",
      emptyText: "Try another status, category, or search query.",
      loadFallbackError: "Could not load room blocks.",
    },
    table: {
      status: "Status",
      room: "Room",
      category: "Category",
      period: "Period",
      reason: "Reason",
      changedBy: "Changed by",
      blockedAt: "Blocked at",
      action: "Action",
      to: "to",
      noSpan: "No span",
      daySpan: (days) => `${days} day${days === 1 ? "" : "s"}`,
      openDraftTitle: "Open reservation draft from this waitlist block",
      openDraftDisabledTitle: "Past waitlist blocks cannot open a reservation draft",
      unblockCategory: "Unblock category",
      unblockRoom: "Unblock room",
      unblockCategoryAria: (name) => `Unblock category ${name}`,
      unblockRoomAria: (name) => `Unblock room ${name}`,
    },
    modal: {
      kicker: "Room blocks",
      title: "Create New Block",
      subtitle:
        "Choose specific rooms or a category-only block, then validate availability before applying it.",
      scopeSectionTitle: "Scope and Dates",
      scopeSectionText: "Select the block scope first, then define the range and operational reason.",
      scopeLabel: "Block Scope",
      scopeOptions: {
        room: "Specific rooms",
        category: "Category only",
      },
      roomCategory: "Room Category",
      addRoom: "Add Room",
      selectCategory: "Select a category",
      noCategoriesAvailable: "No categories available",
      selectRoomToAdd: "Select a room to add",
      allRoomsSelected: "All rooms selected",
      noCategory: "No category",
      startDate: "Start Date",
      endDate: "End Date",
      reason: "Reason",
      reasonPlaceholder: "Operational note for this block...",
      noCategorySelectedYet: "No category selected yet.",
      noRoomsSelectedYet: "No rooms selected yet.",
      removeRoom: (label) => `Remove ${label}`,
      liveSummaryTitle: "Live Summary",
      summaryLabels: {
        rooms: "Rooms",
        categories: "Categories",
        start: "Start",
        span: "Span",
      },
      categoryAvailabilityTitle: "Category Availability",
      categoryAvailabilityText:
        "Validation runs automatically when the selected scope and range are complete.",
      waitingSelection: "Waiting for full selection.",
      noConflicts: "No availability conflicts detected.",
      minAvailable: (minAvail, selected) => `min available: ${minAvail} | rooms to block: ${selected}`,
      occupiedRooms: (rooms) => `occupied rooms: ${rooms.join(", ")}`,
      blockingWarning:
        "Blocking is not possible for one or more categories in this range. Check assignments or choose another scope.",
    },
    status: {
      active: "Active",
      scheduled: "Scheduled",
      past: "Past",
      unknown: "Unknown",
    },
    origin: {
      waitlist: "Waitlist",
      manual: "Manual",
    },
    alerts: {
      selectRoom: "Please select at least one room.",
      selectCategory: "Please select a room category.",
      selectDates: "Please select start and end dates.",
      invalidDateRange: "End date must be on or after start date.",
      createError: "Error creating block.",
      removeTitle: "Remove room block",
      removeConfirm: "Are you sure you want to remove this block?",
      removeHelp: "This releases the blocked inventory immediately. This action cannot be undone.",
      removeError: "Error removing block.",
      openDraftError: "Error opening reservation draft from block.",
      missingReservationGroupId: "Missing reservation_group_id in draft creation response",
    },
    toasts: {
      created: "Room block created successfully.",
      removed: "Room block removed.",
    },
    fallback: {
      categoryBlock: "Category block",
      notAvailable: "N/A",
      category: (id) => `Category ${id}`,
    },
  },
  es: {
    demo: {
      readOnly: "Demo de solo consulta",
      notIncluded: "No incluido en la demo",
    },
    header: {
      kicker: "Centro de operaciones",
      title: "Bloqueos de habitaciones",
      subtitle:
        "Bloquea inventario con filtrado en vivo por estado, visibilidad temporal y validaci\u00f3n de disponibilidad dentro del flujo.",
    },
    buttons: {
      refresh: "Actualizar",
      export: "Exportar Excel",
      newBlock: "Nuevo bloqueo",
      createReservation: "Crear reserva",
      cancel: "Cancelar",
      resetForm: "Reiniciar formulario",
      confirmBlock: "Confirmar bloqueo",
      remove: "Eliminar",
      removing: "Eliminando...",
      closeModal: "Cerrar modal",
    },
    kpis: {
      totalLabel: "Bloqueos totales",
      totalFoot: "Todos los registros de esta unidad de negocio",
      activeLabel: "Activos hoy",
      activeFoot: "Impactando actualmente el inventario vendible",
      scheduledLabel: "Programados",
      scheduledFoot: "Bloqueos futuros que todav\u00eda no han empezado",
      endingLabel: "Terminan en 7 d\u00edas",
      endingFoot: "Buenos candidatos para revisar o ampliar",
      pastLabel: "Pasados",
      pastFoot: "Hist\u00f3rico para trazabilidad",
    },
    filters: {
      statusLabel: "Filtro de estado",
      actionsLabel: "Acciones",
      searchPlaceholder: "Buscar por habitaci\u00f3n, categor\u00eda, motivo, usuario o fecha...",
      allCategories: "Todas las categor\u00edas",
      options: {
        upcoming: "Pr\u00f3ximos",
        active: "Activos",
        scheduled: "Programados",
        past: "Pasados",
        all: "Todos",
      },
    },
    states: {
      loadingTitle: "Cargando bloqueos de habitaciones...",
      loadingText: "Obteniendo habitaciones, estados y metadatos.",
      loadErrorTitle: "No se han podido cargar los datos",
      emptyTitle: "No se han encontrado bloqueos",
      emptyText: "Prueba con otro estado, categor\u00eda o b\u00fasqueda.",
      loadFallbackError: "No se han podido cargar los bloqueos de habitaciones.",
    },
    table: {
      status: "Estado",
      room: "Habitaci\u00f3n",
      category: "Categor\u00eda",
      period: "Periodo",
      reason: "Motivo",
      changedBy: "Modificado por",
      blockedAt: "Bloqueado el",
      action: "Acci\u00f3n",
      to: "a",
      noSpan: "Sin rango",
      daySpan: (days) => `${days} d\u00eda${days === 1 ? "" : "s"}`,
      openDraftTitle: "Abrir borrador de reserva desde este bloqueo de waitlist",
      openDraftDisabledTitle: "Los bloqueos pasados de waitlist no pueden abrir un borrador de reserva",
      unblockCategory: "Desbloquear categor\u00eda",
      unblockRoom: "Desbloquear habitaci\u00f3n",
      unblockCategoryAria: (name) => `Desbloquear categor\u00eda ${name}`,
      unblockRoomAria: (name) => `Desbloquear habitaci\u00f3n ${name}`,
    },
    modal: {
      kicker: "Bloqueos de habitaciones",
      title: "Crear nuevo bloqueo",
      subtitle:
        "Elige habitaciones concretas o un bloqueo solo por categor\u00eda y valida la disponibilidad antes de aplicarlo.",
      scopeSectionTitle: "Alcance y fechas",
      scopeSectionText: "Selecciona primero el alcance del bloqueo y luego define el rango y el motivo operativo.",
      scopeLabel: "Alcance del bloqueo",
      scopeOptions: {
        room: "Habitaciones concretas",
        category: "Solo categor\u00eda",
      },
      roomCategory: "Categor\u00eda de habitaci\u00f3n",
      addRoom: "A\u00f1adir habitaci\u00f3n",
      selectCategory: "Selecciona una categor\u00eda",
      noCategoriesAvailable: "No hay categor\u00edas disponibles",
      selectRoomToAdd: "Selecciona una habitaci\u00f3n para a\u00f1adir",
      allRoomsSelected: "Todas las habitaciones seleccionadas",
      noCategory: "Sin categor\u00eda",
      startDate: "Fecha de inicio",
      endDate: "Fecha de fin",
      reason: "Motivo",
      reasonPlaceholder: "Nota operativa para este bloqueo...",
      noCategorySelectedYet: "Todav\u00eda no hay una categor\u00eda seleccionada.",
      noRoomsSelectedYet: "Todav\u00eda no hay habitaciones seleccionadas.",
      removeRoom: (label) => `Eliminar ${label}`,
      liveSummaryTitle: "Resumen en vivo",
      summaryLabels: {
        rooms: "Habitaciones",
        categories: "Categor\u00edas",
        start: "Inicio",
        span: "Rango",
      },
      categoryAvailabilityTitle: "Disponibilidad por categor\u00eda",
      categoryAvailabilityText:
        "La validaci\u00f3n se ejecuta autom\u00e1ticamente cuando el alcance y el rango seleccionados est\u00e1n completos.",
      waitingSelection: "Esperando a que la selecci\u00f3n est\u00e9 completa.",
      noConflicts: "No se han detectado conflictos de disponibilidad.",
      minAvailable: (minAvail, selected) => `m\u00ednimo disponible: ${minAvail} | habitaciones a bloquear: ${selected}`,
      occupiedRooms: (rooms) => `habitaciones ocupadas: ${rooms.join(", ")}`,
      blockingWarning:
        "No es posible bloquear una o m\u00e1s categor\u00edas en este rango. Revisa las asignaciones o elige otro alcance.",
    },
    status: {
      active: "Activo",
      scheduled: "Programado",
      past: "Pasado",
      unknown: "Desconocido",
    },
    origin: {
      waitlist: "Waitlist",
      manual: "Manual",
    },
    alerts: {
      selectRoom: "Selecciona al menos una habitaci\u00f3n.",
      selectCategory: "Selecciona una categor\u00eda de habitaci\u00f3n.",
      selectDates: "Selecciona las fechas de inicio y fin.",
      invalidDateRange: "La fecha de fin debe ser igual o posterior a la de inicio.",
      createError: "Error al crear el bloqueo.",
      removeTitle: "Eliminar bloqueo de habitaciones",
      removeConfirm: "\u00bfSeguro que quieres eliminar este bloqueo?",
      removeHelp: "El inventario bloqueado se liberar\u00e1 de inmediato. Esta acci\u00f3n no se puede deshacer.",
      removeError: "Error al eliminar el bloqueo.",
      openDraftError: "Error al abrir el borrador de reserva desde el bloqueo.",
      missingReservationGroupId: "Falta reservation_group_id en la respuesta de creaci\u00f3n del borrador",
    },
    toasts: {
      created: "Bloqueo de habitaciones creado correctamente.",
      removed: "Bloqueo de habitaciones eliminado.",
    },
    fallback: {
      categoryBlock: "Bloqueo de categor\u00eda",
      notAvailable: "N/D",
      category: (id) => `Categor\u00eda ${id}`,
    },
  },
  ca: {
    demo: {
      readOnly: "Demo de només consulta",
      notIncluded: "No inclòs a la demo",
    },
    header: {
      kicker: "Centre d'operacions",
      title: "Bloquejos d'habitacions",
      subtitle:
        "Bloqueja inventari amb filtratge en viu per estat, visibilitat temporal i validaci\u00f3 de disponibilitat dins del flux.",
    },
    buttons: {
      refresh: "Actualitzar",
      export: "Exportar Excel",
      newBlock: "Nou bloqueig",
      createReservation: "Crear reserva",
      cancel: "Cancel.lar",
      resetForm: "Reiniciar formulari",
      confirmBlock: "Confirmar bloqueig",
      remove: "Eliminar",
      removing: "Eliminant...",
      closeModal: "Tancar modal",
    },
    kpis: {
      totalLabel: "Bloquejos totals",
      totalFoot: "Tots els registres d'aquesta unitat de negoci",
      activeLabel: "Actius avui",
      activeFoot: "Impactant actualment l'inventari venible",
      scheduledLabel: "Programats",
      scheduledFoot: "Bloquejos futurs que encara no han comen\u00e7at",
      endingLabel: "Acaben en 7 dies",
      endingFoot: "Bons candidats per revisar o ampliar",
      pastLabel: "Passats",
      pastFoot: "Hist\u00f2ric per a tra\u00e7abilitat",
    },
    filters: {
      statusLabel: "Filtre d'estat",
      actionsLabel: "Accions",
      searchPlaceholder: "Cerca per habitaci\u00f3, categoria, motiu, usuari o data...",
      allCategories: "Totes les categories",
      options: {
        upcoming: "Pr\u00f2xims",
        active: "Actius",
        scheduled: "Programats",
        past: "Passats",
        all: "Tots",
      },
    },
    states: {
      loadingTitle: "Carregant bloquejos d'habitacions...",
      loadingText: "Obtenint habitacions, estats i metadades.",
      loadErrorTitle: "No s'han pogut carregar les dades",
      emptyTitle: "No s'han trobat bloquejos",
      emptyText: "Prova amb un altre estat, categoria o cerca.",
      loadFallbackError: "No s'han pogut carregar els bloquejos d'habitacions.",
    },
    table: {
      status: "Estat",
      room: "Habitaci\u00f3",
      category: "Categoria",
      period: "Per\u00edode",
      reason: "Motiu",
      changedBy: "Modificat per",
      blockedAt: "Bloquejat el",
      action: "Acci\u00f3",
      to: "a",
      noSpan: "Sense rang",
      daySpan: (days) => `${days} dia${days === 1 ? "" : "s"}`,
      openDraftTitle: "Obrir esborrany de reserva des d'aquest bloqueig de waitlist",
      openDraftDisabledTitle: "Els bloquejos passats de waitlist no poden obrir un esborrany de reserva",
      unblockCategory: "Desbloquejar categoria",
      unblockRoom: "Desbloquejar habitaci\u00f3",
      unblockCategoryAria: (name) => `Desbloquejar categoria ${name}`,
      unblockRoomAria: (name) => `Desbloquejar habitaci\u00f3 ${name}`,
    },
    modal: {
      kicker: "Bloquejos d'habitacions",
      title: "Crear nou bloqueig",
      subtitle:
        "Tria habitacions concretes o un bloqueig nom\u00e9s per categoria i valida la disponibilitat abans d'aplicar-lo.",
      scopeSectionTitle: "Abast i dates",
      scopeSectionText: "Selecciona primer l'abast del bloqueig i despr\u00e9s defineix el rang i el motiu operatiu.",
      scopeLabel: "Abast del bloqueig",
      scopeOptions: {
        room: "Habitacions concretes",
        category: "Nom\u00e9s categoria",
      },
      roomCategory: "Categoria d'habitaci\u00f3",
      addRoom: "Afegir habitaci\u00f3",
      selectCategory: "Selecciona una categoria",
      noCategoriesAvailable: "No hi ha categories disponibles",
      selectRoomToAdd: "Selecciona una habitaci\u00f3 per afegir",
      allRoomsSelected: "Totes les habitacions seleccionades",
      noCategory: "Sense categoria",
      startDate: "Data d'inici",
      endDate: "Data de fi",
      reason: "Motiu",
      reasonPlaceholder: "Nota operativa per a aquest bloqueig...",
      noCategorySelectedYet: "Encara no hi ha cap categoria seleccionada.",
      noRoomsSelectedYet: "Encara no hi ha habitacions seleccionades.",
      removeRoom: (label) => `Eliminar ${label}`,
      liveSummaryTitle: "Resum en viu",
      summaryLabels: {
        rooms: "Habitacions",
        categories: "Categories",
        start: "Inici",
        span: "Rang",
      },
      categoryAvailabilityTitle: "Disponibilitat per categoria",
      categoryAvailabilityText:
        "La validaci\u00f3 s'executa autom\u00e0ticament quan l'abast i el rang seleccionats estan complets.",
      waitingSelection: "Esperant que la selecci\u00f3 estigui completa.",
      noConflicts: "No s'han detectat conflictes de disponibilitat.",
      minAvailable: (minAvail, selected) => `m\u00ednim disponible: ${minAvail} | habitacions a bloquejar: ${selected}`,
      occupiedRooms: (rooms) => `habitacions ocupades: ${rooms.join(", ")}`,
      blockingWarning:
        "No \u00e9s possible bloquejar una o m\u00e9s categories en aquest rang. Revisa les assignacions o tria un altre abast.",
    },
    status: {
      active: "Actiu",
      scheduled: "Programat",
      past: "Passat",
      unknown: "Desconegut",
    },
    origin: {
      waitlist: "Waitlist",
      manual: "Manual",
    },
    alerts: {
      selectRoom: "Selecciona almenys una habitaci\u00f3.",
      selectCategory: "Selecciona una categoria d'habitaci\u00f3.",
      selectDates: "Selecciona les dates d'inici i fi.",
      invalidDateRange: "La data de fi ha de ser igual o posterior a la d'inici.",
      createError: "Error en crear el bloqueig.",
      removeTitle: "Eliminar bloqueig d'habitacions",
      removeConfirm: "Segur que vols eliminar aquest bloqueig?",
      removeHelp: "L'inventari bloquejat s'alliberar\u00e0 immediatament. Aquesta acci\u00f3 no es pot desfer.",
      removeError: "Error en eliminar el bloqueig.",
      openDraftError: "Error en obrir l'esborrany de reserva des del bloqueig.",
      missingReservationGroupId: "Falta reservation_group_id a la resposta de creaci\u00f3 de l'esborrany",
    },
    toasts: {
      created: "Bloqueig d'habitacions creat correctament.",
      removed: "Bloqueig d'habitacions eliminat.",
    },
    fallback: {
      categoryBlock: "Bloqueig de categoria",
      notAvailable: "N/D",
      category: (id) => `Categoria ${id}`,
    },
  },
};

function formatLocalIso(dateValue) {
  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, "0");
  const day = String(dateValue.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizeIso(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function parseIso(isoDate) {
  if (!isoDate) return null;
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function addDaysIso(isoDate, days) {
  const parsed = parseIso(isoDate);
  if (!parsed) return isoDate;
  parsed.setDate(parsed.getDate() + Number(days || 0));
  return formatLocalIso(parsed);
}

function formatDateLabel(isoDate, locale = undefined) {
  const parsed = parseIso(isoDate);
  if (!parsed) return "-";
  return parsed.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function daysInRange(startIso, endIso) {
  if (!startIso || !endIso) return 0;
  // Hotel date-only periods count calendar days, not local-midnight DST hours.
  const start = new Date(`${startIso}T00:00:00Z`);
  const end = new Date(`${endIso}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  if (end < start) return 0;
  return Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
}

function resolveStatus(startIso, endIso, todayIso) {
  if (!endIso) return "scheduled";
  if (endIso < todayIso) return "past";
  if (startIso && startIso > todayIso) return "scheduled";
  return "active";
}

function getStatusLabel(status, statusMessages) {
  if (status === "active") return statusMessages.active;
  if (status === "scheduled") return statusMessages.scheduled;
  if (status === "past") return statusMessages.past;
  return statusMessages.unknown;
}

function getRowSortWeight(status) {
  if (status === "active") return 0;
  if (status === "scheduled") return 1;
  if (status === "past") return 2;
  return 3;
}

const RoomBlocks = () => {
  const { themeId, languageId, language } = usePmsTheme();
  const isLightTheme = themeId === "light";
  const roomBlocksCopy = ROOM_BLOCKS_MESSAGES[languageId] || ROOM_BLOCKS_MESSAGES.en;
  const calendarLocale = languageId === "en" ? undefined : language?.locale || undefined;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const demoModeActive = isDemoMode();
  const businessUnitId = demoModeActive
    ? DEMO_BUSINESS_UNIT_ID
    : searchParams.get("business_unit_id") || "1";

  const [roomBlocks, setRoomBlocks] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [statusFilter, setStatusFilter] = useState(demoModeActive ? "all" : "upcoming");
  const [searchText, setSearchText] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const [showBlockModal, setShowBlockModal] = useState(false);
  const [pendingRemovalBlock, setPendingRemovalBlock] = useState(null);
  const [removingBlockId, setRemovingBlockId] = useState(null);
  const [blockScope, setBlockScope] = useState("room");
  const [roomPickerValue, setRoomPickerValue] = useState("");
  const [selectedRoomIds, setSelectedRoomIds] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [blockStartDate, setBlockStartDate] = useState("");
  const [blockEndDate, setBlockEndDate] = useState("");
  const [blockReason, setBlockReason] = useState("");
  const [categoryAvailability, setCategoryAvailability] = useState(null);
  const [blockFormError, setBlockFormError] = useState("");

  const [toastMessage, setToastMessage] = useState("");
  const [toastTone, setToastTone] = useState("success");
  const crossTabRefreshToken = useCrossTabDataRefreshToken();
  const toastTimerRef = useRef(null);
  const blockModalRef = useRef(null);
  const removeDialogRef = useRef(null);
  const removingBlockIdRef = useRef(null);
  const localizedStatusFilters = useMemo(
    () =>
      STATUS_FILTERS.map((key) => ({
        key,
        label: roomBlocksCopy.filters.options[key] || key,
      })),
    [roomBlocksCopy]
  );

  const todayIso = useMemo(
    () => (demoModeActive && demoReferenceDate()) || formatLocalIso(new Date()),
    [demoModeActive]
  );
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  const pushToast = useCallback((message, tone = "success") => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    setToastTone(tone);
    setToastMessage(message);
    toastTimerRef.current = setTimeout(() => {
      setToastMessage("");
    }, 2400);
  }, []);

  const loadData = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      setError("");
      try {
        const [roomsRes, blocksRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/rooms?business_unit_id=${businessUnitId}`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/room-blocks?business_unit_id=${businessUnitId}`),
        ]);

        setRooms(Array.isArray(roomsRes.data) ? roomsRes.data : []);
        setRoomBlocks(Array.isArray(blocksRes.data) ? blocksRes.data : []);
      } catch (err) {
        console.error("[RoomBlocks][UI] Error loading data:", err);
        setRooms([]);
        setRoomBlocks([]);
        setError(err?.response?.data?.error || roomBlocksCopy.states.loadFallbackError);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [businessUnitId, roomBlocksCopy.states.loadFallbackError]
  );

  useEffect(() => {
    loadData();
  }, [loadData, crossTabRefreshToken]);

  const decoratedBlocks = useMemo(() => {
    const rows = Array.isArray(roomBlocks) ? roomBlocks : [];
    return rows
      .map((block) => {
        const startIso = normalizeIso(block?.start_date);
        const endIso = normalizeIso(block?.end_date);
        const createdIso = normalizeIso(block?.created_at);
        const status = resolveStatus(startIso, endIso, todayIso);
        const isCategoryOnlyBlock = block?.room_id == null;
        const waitlistEntry = !demoModeActive && Array.isArray(block?.waitlist_entries)
          ? block.waitlist_entries[0] || null : null;
        const roomNumber = String(
          block?.room?.room_number ||
            (isCategoryOnlyBlock ? roomBlocksCopy.fallback.categoryBlock : roomBlocksCopy.fallback.notAvailable)
        );
        const categoryName = String(
          block?.RoomCategory?.name || block?.room?.RoomCategory?.name || roomBlocksCopy.fallback.notAvailable
        );
        const reason = demoModeActive && !Object.prototype.hasOwnProperty.call(block, "reason")
          ? roomBlocksCopy.demo.notIncluded : String(block?.reason || "").trim();
        const changedBy = demoModeActive && !Object.prototype.hasOwnProperty.call(block, "changed_by")
          ? roomBlocksCopy.demo.notIncluded : String(block?.changed_by || "-");
        const createdLabel = demoModeActive && !Object.prototype.hasOwnProperty.call(block, "created_at")
          ? roomBlocksCopy.demo.notIncluded : formatDateLabel(createdIso, calendarLocale);
        const daySpan = daysInRange(startIso, endIso);
        return {
          ...block,
          startIso,
          endIso,
          createdIso,
          status,
          statusLabel: getStatusLabel(status, roomBlocksCopy.status),
          sortWeight: getRowSortWeight(status),
          isCategoryOnlyBlock,
          roomNumber,
          categoryName,
          reason,
          changedBy,
          createdLabel,
          daySpan,
          waitlistEntry,
          originType: demoModeActive ? null
            : waitlistEntry ? roomBlocksCopy.origin.waitlist : roomBlocksCopy.origin.manual,
          canCreateReservation: !demoModeActive && Boolean(waitlistEntry) && status !== "past",
        };
      })
      .sort((a, b) => {
        if (a.sortWeight !== b.sortWeight) return a.sortWeight - b.sortWeight;
        if (a.status === "past") {
          return String(b.endIso || "").localeCompare(String(a.endIso || ""));
        }
        return String(a.startIso || "").localeCompare(String(b.startIso || ""));
      });
  }, [roomBlocks, roomBlocksCopy, todayIso, demoModeActive, calendarLocale]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    decoratedBlocks.forEach((block) => {
      if (block.categoryName && block.categoryName !== roomBlocksCopy.fallback.notAvailable) set.add(block.categoryName);
    });
    return Array.from(set).sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { sensitivity: "base" })
    );
  }, [decoratedBlocks, roomBlocksCopy.fallback.notAvailable]);

  const stats = useMemo(() => {
    const soonLimit = addDaysIso(todayIso, 7);
    const out = {
      total: decoratedBlocks.length,
      active: 0,
      scheduled: 0,
      endingSoon: 0,
      past: 0,
    };

    decoratedBlocks.forEach((block) => {
      if (block.status === "active") out.active += 1;
      if (block.status === "scheduled") out.scheduled += 1;
      if (block.status === "past") out.past += 1;
      if (block.endIso && block.endIso >= todayIso && block.endIso <= soonLimit) {
        out.endingSoon += 1;
      }
    });

    return out;
  }, [decoratedBlocks, todayIso]);

  const filteredBlocks = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return decoratedBlocks.filter((block) => {
      if (statusFilter === "upcoming" && block.status === "past") return false;
      if (statusFilter !== "upcoming" && statusFilter !== "all" && block.status !== statusFilter) {
        return false;
      }

      if (categoryFilter !== "all" && block.categoryName !== categoryFilter) return false;

      if (!search) return true;
      const haystack = [
        block.roomNumber,
        block.categoryName,
        block.reason,
        block.changedBy,
        block.startIso,
        block.endIso,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(search);
    });
  }, [decoratedBlocks, statusFilter, categoryFilter, searchText]);

  const roomById = useMemo(() => {
    const map = new Map();
    (Array.isArray(rooms) ? rooms : []).forEach((room) => {
      map.set(String(room.id), room);
    });
    return map;
  }, [rooms]);

  const selectedRooms = useMemo(() => {
    return selectedRoomIds
      .map((roomId) => roomById.get(String(roomId)))
      .filter(Boolean);
  }, [selectedRoomIds, roomById]);

  const selectableRooms = useMemo(() => {
    const selectedSet = new Set(selectedRoomIds.map((id) => String(id)));
    return (Array.isArray(rooms) ? rooms : [])
      .filter((room) => !selectedSet.has(String(room.id)))
      .sort((a, b) =>
        String(a.room_number || "").localeCompare(String(b.room_number || ""), undefined, {
          numeric: true,
          sensitivity: "base",
        })
      );
  }, [rooms, selectedRoomIds]);

  const categoryChoices = useMemo(() => {
    const map = new Map();
    (Array.isArray(rooms) ? rooms : []).forEach((room) => {
      const categoryId = room?.room_category_id;
      if (categoryId == null || map.has(String(categoryId))) return;
      map.set(String(categoryId), {
        id: String(categoryId),
        name: room?.RoomCategory?.name || roomBlocksCopy.fallback.category(categoryId),
      });
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), undefined, {
        sensitivity: "base",
      })
    );
  }, [rooms, roomBlocksCopy.fallback]);

  const selectedCategory = useMemo(() => {
    return categoryChoices.find((category) => String(category.id) === String(selectedCategoryId)) || null;
  }, [categoryChoices, selectedCategoryId]);

  const selectedRoomCategoryCount = useMemo(() => {
    if (blockScope === "category") {
      return selectedCategoryId ? 1 : 0;
    }
    const set = new Set();
    selectedRooms.forEach((room) => {
      if (room?.RoomCategory?.name) set.add(room.RoomCategory.name);
      else if (room?.room_category_id != null) set.add(String(room.room_category_id));
    });
    return set.size;
  }, [blockScope, selectedCategoryId, selectedRooms]);

  const selectedBlockUnits = useMemo(() => {
    if (blockScope === "category") return selectedCategoryId ? 1 : 0;
    return selectedRoomIds.length;
  }, [blockScope, selectedCategoryId, selectedRoomIds.length]);

  const selectedSpanDays = useMemo(() => {
    return daysInRange(blockStartDate, blockEndDate);
  }, [blockStartDate, blockEndDate]);

  const blockNotPossible = useMemo(() => {
    if (!Array.isArray(categoryAvailability)) return false;
    return categoryAvailability.some((info) => {
      const minAvail = Number(info?.min_available_rooms_in_range || 0);
      const selected = Number(info?.selected_rooms_in_category || 0);
      const occupiedRooms = Array.isArray(info?.occupied_rooms) ? info.occupied_rooms : [];
      return minAvail < selected || occupiedRooms.length > 0;
    });
  }, [categoryAvailability]);
  const verifyCategoryAvailability = useCallback(
    async (endDateOverride) => {
      if (demoModeActive) return;
      const endDate = endDateOverride || blockEndDate;
      const hasScopeSelection =
        blockScope === "category" ? Boolean(selectedCategoryId) : selectedRoomIds.length > 0;
      if (!hasScopeSelection || !blockStartDate || !endDate) {
        setCategoryAvailability(null);
        return;
      }
      try {
        const params = {
          business_unit_id: businessUnitId,
          start_date: blockStartDate,
          end_date: endDate,
        };
        if (blockScope === "category") {
          params.room_category_id = selectedCategoryId;
        } else {
          params.room_ids = selectedRoomIds.join(",");
        }
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/room-blocks/category-availability`,
          {
            params,
          }
        );
        setCategoryAvailability(Array.isArray(res?.data?.by_category) ? res.data.by_category : []);
      } catch (err) {
        console.error("[RoomBlocks][UI][Verify] Availability check failed:", err);
        setCategoryAvailability(null);
      }
    },
    [blockEndDate, blockScope, blockStartDate, businessUnitId, selectedCategoryId, selectedRoomIds, demoModeActive]
  );

  useEffect(() => {
    if (demoModeActive || !showBlockModal) return;
    if (!blockEndDate) {
      setCategoryAvailability(null);
      return;
    }
    verifyCategoryAvailability(blockEndDate);
  }, [showBlockModal, blockScope, blockStartDate, blockEndDate, selectedCategoryId, selectedRoomIds, verifyCategoryAvailability, demoModeActive]);

  useEffect(() => {
    setCategoryAvailability(null);
    if (blockScope === "category") {
      setRoomPickerValue("");
      setSelectedRoomIds([]);
      return;
    }
    setSelectedCategoryId("");
  }, [blockScope]);

  const resetBlockForm = useCallback(() => {
    setBlockScope("room");
    setRoomPickerValue("");
    setSelectedRoomIds([]);
    setSelectedCategoryId("");
    setBlockStartDate("");
    setBlockEndDate("");
    setBlockReason("");
    setCategoryAvailability(null);
    setBlockFormError("");
  }, []);

  const closeBlockModal = useCallback(() => {
    setShowBlockModal(false);
    resetBlockForm();
  }, [resetBlockForm]);

  useEffect(() => {
    if (demoModeActive || !showBlockModal) return undefined;

    const previousFocus = document.activeElement;
    const dialog = blockModalRef.current;
    const focusableSelector =
      'button:not(:disabled), select:not(:disabled), input:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"]):not(:disabled)';
    dialog?.querySelector(focusableSelector)?.focus();

    const handleDialogKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeBlockModal();
        return;
      }
      if (event.key !== "Tab" || !dialog) return;
      const focusable = Array.from(dialog.querySelectorAll(focusableSelector));
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleDialogKeyDown);
    return () => {
      document.removeEventListener("keydown", handleDialogKeyDown);
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    };
  }, [showBlockModal, demoModeActive, closeBlockModal]);

  useEffect(() => {
    if (demoModeActive || !pendingRemovalBlock) return undefined;

    const previousFocus = document.activeElement;
    const dialog = removeDialogRef.current;
    const focusableSelector =
      'button:not(:disabled), [tabindex]:not([tabindex="-1"]):not(:disabled)';
    const initialFocus = dialog?.querySelector(focusableSelector);
    if (initialFocus instanceof HTMLElement) initialFocus.focus();
    else dialog?.focus();

    const handleRemoveDialogKeyDown = (event) => {
      if (event.key === "Escape" && !removingBlockIdRef.current) {
        event.preventDefault();
        setPendingRemovalBlock(null);
        return;
      }
      if (event.key !== "Tab" || !dialog) return;
      const focusable = Array.from(dialog.querySelectorAll(focusableSelector));
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleRemoveDialogKeyDown);
    return () => {
      document.removeEventListener("keydown", handleRemoveDialogKeyDown);
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    };
  }, [pendingRemovalBlock, demoModeActive]);

  const handleAddRoom = (roomId) => {
    if (demoModeActive) return;
    if (!roomId) return;
    setSelectedRoomIds((prev) => {
      const key = String(roomId);
      if (prev.some((item) => String(item) === key)) return prev;
      return [...prev, key];
    });
    setRoomPickerValue("");
  };

  const handleRemoveRoom = (roomId) => {
    if (demoModeActive) return;
    setSelectedRoomIds((prev) => prev.filter((id) => String(id) !== String(roomId)));
  };

  const handleConfirmBlock = async () => {
    if (demoModeActive) return;
    setBlockFormError("");
    if (blockScope === "room" && selectedRoomIds.length === 0) {
      setBlockFormError(roomBlocksCopy.alerts.selectRoom);
      return;
    }
    if (blockScope === "category" && !selectedCategoryId) {
      setBlockFormError(roomBlocksCopy.alerts.selectCategory);
      return;
    }
    if (!blockStartDate || !blockEndDate) {
      setBlockFormError(roomBlocksCopy.alerts.selectDates);
      return;
    }
    if (blockEndDate < blockStartDate) {
      setBlockFormError(roomBlocksCopy.alerts.invalidDateRange);
      return;
    }
    if (blockNotPossible) return;

    try {
      const payload = {
        start_date: blockStartDate,
        end_date: blockEndDate,
        reason: blockReason,
      };
      if (blockScope === "category") {
        payload.room_category_id = selectedCategoryId;
      } else {
        payload.room_ids = selectedRoomIds;
      }
      await axios.post(`${process.env.REACT_APP_API_URL}/api/room-blocks`, payload);
      closeBlockModal();
      await loadData({ silent: true });
      pushToast(roomBlocksCopy.toasts.created);
    } catch (err) {
      console.error("[RoomBlocks][UI] Error creating room block:", err);
      setBlockFormError(err?.response?.data?.error || roomBlocksCopy.alerts.createError);
    }
  };

  const handleUnblock = async (block) => {
    if (demoModeActive) return;
    const id = block?.id;
    if (!id || removingBlockIdRef.current) return;
    removingBlockIdRef.current = id;
    setRemovingBlockId(id);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/room-blocks/${id}`);
      setRoomBlocks((prev) => prev.filter((item) => item.id !== id));
      setPendingRemovalBlock(null);
      pushToast(roomBlocksCopy.toasts.removed);
    } catch (err) {
      console.error("[RoomBlocks][UI] Error unblocking room:", err);
      pushToast(err?.response?.data?.error || roomBlocksCopy.alerts.removeError, "error");
    } finally {
      removingBlockIdRef.current = null;
      setRemovingBlockId(null);
    }
  };

  const handleCreateReservationFromBlock = async (block) => {
    if (demoModeActive) return;
    if (!block?.id || !businessUnitId) return;
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/reservation-drafts/create`, {
        number_of_rooms: 1,
        created_by: "RoomBlocksCreateReservation",
        business_unit_id: Number(businessUnitId),
        source_block_id: Number(block.id),
        source_waitlist_entry_id: block?.waitlistEntry?.id || null,
      });

      const reservationGroupId = res?.data?.reservation_group_id;
      if (!reservationGroupId) {
        throw new Error(roomBlocksCopy.alerts.missingReservationGroupId);
      }

      navigate(`/reservation-group/${reservationGroupId}`);
    } catch (err) {
      console.error("[RoomBlocks][UI] Error creating reservation draft from block:", err);
      pushToast(err?.response?.data?.error || roomBlocksCopy.alerts.openDraftError, "error");
    }
  };

  const handleExport = () => {
    const rows = filteredBlocks.map((block) => ({
      [roomBlocksCopy.table.status]: block.statusLabel,
      [roomBlocksCopy.table.room]: block.roomNumber,
      [roomBlocksCopy.table.category]: block.categoryName,
      [roomBlocksCopy.table.period]: block.startIso && block.endIso ? `${block.startIso} - ${block.endIso}` : "-",
      [roomBlocksCopy.table.reason]: block.reason || "-",
      [roomBlocksCopy.table.changedBy]: block.changedBy,
      [roomBlocksCopy.table.blockedAt]: demoModeActive && !Object.prototype.hasOwnProperty.call(block, "created_at")
        ? roomBlocksCopy.demo.notIncluded : block.createdIso || "-",
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Room Blocks");
    const exportDate = demoModeActive ? todayIso : formatLocalIso(new Date());
    XLSX.writeFile(workbook, `room-blocks-${exportDate}.xlsx`);
  };

  return (
    <div className={`rb-shell${isLightTheme ? " rb-shell--light" : ""}`}>
      <section className="rb-stage">
        <header className="rb-head">
          <div>
            <p className="rb-kicker">{roomBlocksCopy.header.kicker}</p>
            <h1 className="rb-title">{roomBlocksCopy.header.title}</h1>
            <p className="rb-subtitle">{roomBlocksCopy.header.subtitle}</p>
          </div>

          <div className="rb-kpiGrid">
            <article className="rb-kpiCard rb-kpiCard--cyan">
              <div className="rb-kpiLabel">{roomBlocksCopy.kpis.totalLabel}</div>
              <div className="rb-kpiValue">{stats.total}</div>
              <div className="rb-kpiFoot">{roomBlocksCopy.kpis.totalFoot}</div>
            </article>
            <article className="rb-kpiCard rb-kpiCard--green">
              <div className="rb-kpiLabel">{roomBlocksCopy.kpis.activeLabel}</div>
              <div className="rb-kpiValue">{stats.active}</div>
              <div className="rb-kpiFoot">{roomBlocksCopy.kpis.activeFoot}</div>
            </article>
            <article className="rb-kpiCard rb-kpiCard--blue">
              <div className="rb-kpiLabel">{roomBlocksCopy.kpis.scheduledLabel}</div>
              <div className="rb-kpiValue">{stats.scheduled}</div>
              <div className="rb-kpiFoot">{roomBlocksCopy.kpis.scheduledFoot}</div>
            </article>
            <article className="rb-kpiCard rb-kpiCard--ending">
              <div className="rb-kpiLabel">{roomBlocksCopy.kpis.endingLabel}</div>
              <div className="rb-kpiValue">{stats.endingSoon}</div>
              <div className="rb-kpiFoot">{roomBlocksCopy.kpis.endingFoot}</div>
            </article>
            <article className="rb-kpiCard rb-kpiCard--slate">
              <div className="rb-kpiLabel">{roomBlocksCopy.kpis.pastLabel}</div>
              <div className="rb-kpiValue">{stats.past}</div>
              <div className="rb-kpiFoot">{roomBlocksCopy.kpis.pastFoot}</div>
            </article>
          </div>
        </header>

        <div className="rb-content">
          <div className="rb-filterRow">
          <section className="rb-filterPanel">
            <div className="rb-filterTop">
              <span className="rb-filterLabel">{roomBlocksCopy.filters.statusLabel}</span>
              <div className="rb-pillRow">
                {localizedStatusFilters.map((filter) => (
                  <button
                    key={filter.key}
                    type="button"
                    className={`rb-pill ${statusFilter === filter.key ? "is-active" : ""}`}
                    onClick={() => setStatusFilter(filter.key)}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="rb-filterGrid">
              <input
                type="text"
                className="rb-input"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder={roomBlocksCopy.filters.searchPlaceholder}
              />
              <select
                className="rb-select"
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
              >
                <option value="all">{roomBlocksCopy.filters.allCategories}</option>
                {categoryOptions.map((categoryName) => (
                  <option key={categoryName} value={categoryName}>
                    {categoryName}
                  </option>
                ))}
              </select>
            </div>
          </section>

            <div className="rb-actionsPanel">
              <span className="rb-filterLabel">{roomBlocksCopy.filters.actionsLabel}</span>
              <div className="rb-actionButtons">
                <button
                  type="button"
                  className="rb-btn rb-btn--primary"
                  onClick={() => { if (!demoModeActive) setShowBlockModal(true); }}
                  disabled={demoModeActive}
                  title={demoModeActive ? roomBlocksCopy.demo.readOnly : undefined}
                >
                  {roomBlocksCopy.buttons.newBlock}
                </button>
                <button type="button" className="rb-btn" onClick={handleExport}>
                  {roomBlocksCopy.buttons.export}
                </button>
                <button type="button" className="rb-btn" onClick={() => loadData()}>
                  {roomBlocksCopy.buttons.refresh}
                </button>
              </div>
            </div>
          </div>

          <div className="rb-tableWrap">
            {loading ? (
              <div className="rb-state">
                <div>
                  <div className="rb-stateTitle">{roomBlocksCopy.states.loadingTitle}</div>
                  <div className="rb-stateText">{roomBlocksCopy.states.loadingText}</div>
                </div>
              </div>
            ) : error ? (
              <div className="rb-state">
                <div>
                  <div className="rb-stateTitle">{roomBlocksCopy.states.loadErrorTitle}</div>
                  <div className="rb-stateText">{error}</div>
                </div>
              </div>
            ) : filteredBlocks.length === 0 ? (
              <div className="rb-state">
                <div>
                  <div className="rb-stateTitle">{roomBlocksCopy.states.emptyTitle}</div>
                  <div className="rb-stateText">{roomBlocksCopy.states.emptyText}</div>
                </div>
              </div>
            ) : (
              <table className="rb-table">
                <thead>
                  <tr>
                    <th>{roomBlocksCopy.table.status}</th>
                    <th>{roomBlocksCopy.table.room}</th>
                    <th>{roomBlocksCopy.table.category}</th>
                    <th>{roomBlocksCopy.table.period}</th>
                    <th>{roomBlocksCopy.table.reason}</th>
                    <th>{roomBlocksCopy.table.changedBy}</th>
                    <th>{roomBlocksCopy.table.blockedAt}</th>
                    <th>{roomBlocksCopy.table.action}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBlocks.map((block) => (
                    <tr key={block.id} className={`rb-row rb-row--${block.status}`}>
                      <td>
                        <span className={`rb-badge rb-badge--${block.status}`}>{block.statusLabel}</span>
                      </td>
                      <td className="rb-roomCell">{block.roomNumber}</td>
                      <td>{block.categoryName}</td>
                      <td>
                        <div className="rb-period">
                          {formatDateLabel(block.startIso, calendarLocale)} {roomBlocksCopy.table.to}{" "}
                          {formatDateLabel(block.endIso, calendarLocale)}
                        </div>
                        <div className="rb-muted">
                          {block.daySpan > 0 ? roomBlocksCopy.table.daySpan(block.daySpan) : roomBlocksCopy.table.noSpan}
                        </div>
                      </td>
                      <td>
                        <div className="rb-reason" title={block.reason || "-"}>
                          {block.reason || "-"}
                        </div>
                        {!demoModeActive && <div className="rb-originMeta">
                          {block.originType}
                          {block.waitlistEntry?.guest_name ? ` · ${block.waitlistEntry.guest_name}` : ""}
                        </div>}
                      </td>
                      <td>{block.changedBy || "-"}</td>
                      <td>{block.createdLabel}</td>
                      <td>
                        <div className="rb-rowActions">
                          {!demoModeActive && block.waitlistEntry ? (
                            <button
                              type="button"
                              className="rb-inlineBtn"
                              disabled={!block.canCreateReservation}
                              onClick={() => handleCreateReservationFromBlock(block)}
                              title={
                                block.canCreateReservation
                                  ? roomBlocksCopy.table.openDraftTitle
                                  : roomBlocksCopy.table.openDraftDisabledTitle
                              }
                            >
                              {roomBlocksCopy.buttons.createReservation}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="rb-actionIcon"
                            onClick={() => { if (!demoModeActive) setPendingRemovalBlock(block); }}
                            disabled={demoModeActive || Boolean(removingBlockId)}
                            aria-label={
                              block.isCategoryOnlyBlock
                                ? roomBlocksCopy.table.unblockCategoryAria(block.categoryName)
                                : roomBlocksCopy.table.unblockRoomAria(block.roomNumber)
                            }
                            title={
                              demoModeActive ? roomBlocksCopy.demo.readOnly : block.isCategoryOnlyBlock
                                ? roomBlocksCopy.table.unblockCategory
                                : roomBlocksCopy.table.unblockRoom
                            }
                          >
                            ×
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>

      {!demoModeActive && showBlockModal && (
        <div className="rb-modalOverlay">
          <div
            ref={blockModalRef}
            className="rb-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rb-modal-title"
            aria-describedby="rb-modal-subtitle"
            tabIndex={-1}
          >
            <div className="rb-modalHead">
              <div>
                <p className="rb-modalKicker">{roomBlocksCopy.modal.kicker}</p>
                <h2 id="rb-modal-title" className="rb-modalTitle">{roomBlocksCopy.modal.title}</h2>
                <p id="rb-modal-subtitle" className="rb-modalSub">{roomBlocksCopy.modal.subtitle}</p>
              </div>
              <button
                type="button"
                className="rb-modalClose"
                onClick={closeBlockModal}
                aria-label={roomBlocksCopy.buttons.closeModal}
              >
                ×
              </button>
            </div>

            <div className="rb-modalBody">
              <div>
                <section className="rb-modalSection">
                  <h3 className="rb-modalSectionTitle">{roomBlocksCopy.modal.scopeSectionTitle}</h3>
                  <p className="rb-modalSectionText">{roomBlocksCopy.modal.scopeSectionText}</p>

                  <div className="rb-modalGrid rb-modalGrid--blockForm">
                    <div>
                      <label className="rb-modalFieldLabel">{roomBlocksCopy.modal.scopeLabel}</label>
                      <select
                        className="rb-modalSelect"
                        value={blockScope}
                        disabled={blockStartDate !== "" || blockEndDate !== ""}
                        onChange={(event) => setBlockScope(event.target.value)}
                      >
                        <option value="room">{roomBlocksCopy.modal.scopeOptions.room}</option>
                        <option value="category">{roomBlocksCopy.modal.scopeOptions.category}</option>
                      </select>
                    </div>

                    <div>
                      <label className="rb-modalFieldLabel">
                        {blockScope === "category" ? roomBlocksCopy.modal.roomCategory : roomBlocksCopy.modal.addRoom}
                      </label>
                      {blockScope === "category" ? (
                        <select
                          className="rb-modalSelect rb-modalSelect--lightText"
                          value={selectedCategoryId}
                          disabled={categoryChoices.length === 0 || blockStartDate !== ""}
                          onChange={(event) => setSelectedCategoryId(event.target.value)}
                        >
                          <option value="">
                            {categoryChoices.length
                              ? roomBlocksCopy.modal.selectCategory
                              : roomBlocksCopy.modal.noCategoriesAvailable}
                          </option>
                          {categoryChoices.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <select
                          className="rb-modalSelect rb-modalSelect--lightText"
                          value={roomPickerValue}
                          disabled={selectableRooms.length === 0 || blockStartDate !== ""}
                          onChange={(event) => handleAddRoom(event.target.value)}
                        >
                          <option value="">
                            {selectableRooms.length
                              ? roomBlocksCopy.modal.selectRoomToAdd
                              : roomBlocksCopy.modal.allRoomsSelected}
                          </option>
                          {selectableRooms.map((room) => (
                            <option key={room.id} value={String(room.id)}>
                              {room.room_number} ({room.RoomCategory?.name || roomBlocksCopy.modal.noCategory})
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    <div>
                      <label className="rb-modalFieldLabel">{roomBlocksCopy.modal.startDate}</label>
                      <input
                        type="date"
                        className="rb-modalInput"
                        value={blockStartDate}
                        disabled={
                          (blockScope === "category" ? !selectedCategoryId : selectedRoomIds.length === 0) ||
                          blockEndDate !== ""
                        }
                        onChange={(event) => setBlockStartDate(event.target.value)}
                      />
                    </div>

                    <div>
                      <label className="rb-modalFieldLabel">{roomBlocksCopy.modal.endDate}</label>
                      <input
                        type="date"
                        className="rb-modalInput"
                        value={blockEndDate}
                        disabled={!blockStartDate}
                        onChange={(event) => setBlockEndDate(event.target.value)}
                      />
                    </div>

                    <div className="rb-modalField--wide">
                      <label className="rb-modalFieldLabel">{roomBlocksCopy.modal.reason}</label>
                      <textarea
                        className="rb-modalTextarea"
                        value={blockReason}
                        onChange={(event) => setBlockReason(event.target.value)}
                        placeholder={roomBlocksCopy.modal.reasonPlaceholder}
                      />
                    </div>
                  </div>

                  <div className="rb-chipWrap">
                    {blockScope === "category" ? (
                      selectedCategory ? (
                        <span className="rb-chip">{selectedCategory.name}</span>
                      ) : (
                        <span className="rb-muted">{roomBlocksCopy.modal.noCategorySelectedYet}</span>
                      )
                    ) : selectedRooms.length === 0 ? (
                      <span className="rb-muted">{roomBlocksCopy.modal.noRoomsSelectedYet}</span>
                    ) : (
                      selectedRooms.map((room) => {
                        const label = `${room.room_number} (${room.RoomCategory?.name || roomBlocksCopy.modal.noCategory})`;
                        return (
                          <span key={room.id} className="rb-chip">
                            {label}
                            <button
                              type="button"
                              className="rb-chipRemove"
                              onClick={() => handleRemoveRoom(room.id)}
                              aria-label={roomBlocksCopy.modal.removeRoom(label)}
                              title={roomBlocksCopy.buttons.remove}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })
                    )}
                  </div>

                  {blockFormError ? (
                    <div className="rb-modalWarning" role="alert">
                      {blockFormError}
                    </div>
                  ) : null}
                </section>
              </div>

              <aside>
                <section className="rb-modalSection">
                  <h3 className="rb-modalSectionTitle">{roomBlocksCopy.modal.liveSummaryTitle}</h3>
                  <div className="rb-modalSummaryGrid">
                    <div className="rb-summaryItem">
                      <div className="rb-summaryLabel">{roomBlocksCopy.modal.summaryLabels.rooms}</div>
                      <div className="rb-summaryValue">{selectedBlockUnits}</div>
                    </div>
                    <div className="rb-summaryItem">
                      <div className="rb-summaryLabel">{roomBlocksCopy.modal.summaryLabels.categories}</div>
                      <div className="rb-summaryValue">{selectedRoomCategoryCount}</div>
                    </div>
                    <div className="rb-summaryItem">
                      <div className="rb-summaryLabel">{roomBlocksCopy.modal.summaryLabels.start}</div>
                      <div className="rb-summaryValue">
                        {blockStartDate ? formatDateLabel(blockStartDate, calendarLocale) : "-"}
                      </div>
                    </div>
                    <div className="rb-summaryItem">
                      <div className="rb-summaryLabel">{roomBlocksCopy.modal.summaryLabels.span}</div>
                      <div className="rb-summaryValue">{selectedSpanDays > 0 ? `${selectedSpanDays}d` : "-"}</div>
                    </div>
                  </div>
                </section>

                <section className="rb-modalSection">
                  <h3 className="rb-modalSectionTitle">{roomBlocksCopy.modal.categoryAvailabilityTitle}</h3>
                  <p className="rb-modalSectionText">{roomBlocksCopy.modal.categoryAvailabilityText}</p>

                  {categoryAvailability == null ? (
                    <div className="rb-muted rb-muted--spaced">
                      {roomBlocksCopy.modal.waitingSelection}
                    </div>
                  ) : categoryAvailability.length === 0 ? (
                    <div className="rb-muted rb-muted--spaced">
                      {roomBlocksCopy.modal.noConflicts}
                    </div>
                  ) : (
                    <div className="rb-availabilityList">
                      {categoryAvailability.map((info) => {
                        const occupiedRooms = Array.isArray(info?.occupied_rooms) ? info.occupied_rooms : [];
                        const minAvail = Number(info?.min_available_rooms_in_range || 0);
                        const selected = Number(info?.selected_rooms_in_category || 0);
                        const invalid = minAvail < selected || occupiedRooms.length > 0;
                        const label =
                          info?.room_category_name || roomBlocksCopy.fallback.category(info?.room_category_id);
                        return (
                          <div
                            key={`${info?.room_category_id}-${label}`}
                            className={`rb-availabilityItem ${invalid ? "is-blocked" : "is-ok"}`}
                          >
                            <div className="rb-availabilityTitle">{label}</div>
                            <div className="rb-availabilityBody">
                              {roomBlocksCopy.modal.minAvailable(minAvail, selected)}
                            </div>
                            {occupiedRooms.length > 0 ? (
                              <div className="rb-availabilityBody">
                                {roomBlocksCopy.modal.occupiedRooms(occupiedRooms)}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {blockNotPossible ? (
                    <div className="rb-modalWarning">
                      {roomBlocksCopy.modal.blockingWarning}
                    </div>
                  ) : null}
                </section>
              </aside>
            </div>

            <div className="rb-modalFooter">
              <button type="button" className="rb-modalBtn" onClick={closeBlockModal}>
                {roomBlocksCopy.buttons.cancel}
              </button>
              <button type="button" className="rb-modalBtn rb-modalBtn--warn" onClick={resetBlockForm}>
                {roomBlocksCopy.buttons.resetForm}
              </button>
              <button
                type="button"
                className="rb-modalBtn rb-modalBtn--primary"
                onClick={handleConfirmBlock}
                disabled={blockNotPossible}
              >
                {roomBlocksCopy.buttons.confirmBlock}
              </button>
            </div>
          </div>
        </div>
      )}

      {!demoModeActive && pendingRemovalBlock ? (
        <div className="rb-confirmOverlay">
          <div
            ref={removeDialogRef}
            className="rb-confirm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="rb-remove-title"
            aria-describedby="rb-remove-description"
            tabIndex={-1}
          >
            <div className="rb-confirmMark" aria-hidden="true">!</div>
            <div className="rb-confirmContent">
              <p className="rb-modalKicker">{roomBlocksCopy.modal.kicker}</p>
              <h2 id="rb-remove-title" className="rb-confirmTitle">
                {roomBlocksCopy.alerts.removeTitle}
              </h2>
              <p id="rb-remove-description" className="rb-confirmText">
                {roomBlocksCopy.alerts.removeConfirm} {roomBlocksCopy.alerts.removeHelp}
              </p>
              <dl className="rb-confirmSummary">
                <div>
                  <dt>{roomBlocksCopy.table.room}</dt>
                  <dd>{pendingRemovalBlock.roomNumber}</dd>
                </div>
                <div>
                  <dt>{roomBlocksCopy.table.category}</dt>
                  <dd>{pendingRemovalBlock.categoryName}</dd>
                </div>
                <div>
                  <dt>{roomBlocksCopy.table.period}</dt>
                  <dd>
                    {formatDateLabel(pendingRemovalBlock.startIso, calendarLocale)} {roomBlocksCopy.table.to}{" "}
                    {formatDateLabel(pendingRemovalBlock.endIso, calendarLocale)}
                  </dd>
                </div>
              </dl>
              <div className="rb-confirmActions">
                <button
                  type="button"
                  className="rb-modalBtn"
                  onClick={() => setPendingRemovalBlock(null)}
                  disabled={Boolean(removingBlockId)}
                >
                  {roomBlocksCopy.buttons.cancel}
                </button>
                <button
                  type="button"
                  className="rb-modalBtn rb-modalBtn--danger"
                  onClick={() => handleUnblock(pendingRemovalBlock)}
                  disabled={Boolean(removingBlockId)}
                >
                  {removingBlockId ? roomBlocksCopy.buttons.removing : roomBlocksCopy.buttons.remove}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {toastMessage ? (
        <div className="rb-toastOverlay" role="status" aria-live="polite">
          <div className={`rb-toastCard rb-toastCard--${toastTone}`}>{toastMessage}</div>
        </div>
      ) : null}
    </div>
  );
};

export default RoomBlocks;
